import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * CrashClawChatPanel — CrashClaw 模式的聊天子组件
 *
 * 与 AI Agent 模式的聊天逻辑完全独立：拥有自己的会话状态、消息历史、workspace 选择等。
 *
 * 功能：
 *   - 加载用户 Workspace 列表，判断是否已配置
 *   - 未配置：显示引导卡片 + "前往配置"按钮（在新页面打开个人设置的 CrashClaw tab）
 *   - 已配置：显示 Workspace 下拉选择器
 *   - 选择 Workspace 后，可进行流式聊天（通过 Bridge 中继到 CoPaw）
 *   - 支持会话历史列表（通过 Bridge 代理 CoPaw GET /api/chats）
 *   - 支持选择历史会话加载消息（通过 Bridge 代理 CoPaw GET /api/chats/{chatId}）
 *   - 支持会话继续（复用历史会话的 session_id）
 *
 * 后端接口：
 *   - POST /redir/api/crashclaw/listWorkspaces          → Workspace 列表
 *   - POST /redir/api/crashclaw/chat                     → SSE 流式聊天（AG-UI 协议）
 *   - POST /redir/api/crashclaw/listCopawSessions        → 会话历史列表
 *   - POST /redir/api/crashclaw/getCopawSessionMessages   → 查询指定会话的消息历史
 *   - POST /redir/api/crashclaw/updateCopawSession        → 重命名会话
 *   - POST /redir/api/crashclaw/deleteCopawSession        → 删除会话
 */
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Button, Divider, Popover, Tag, Alert, message } from 'antd';
import { ArrowRightOutlined, ExclamationCircleOutlined, CloudOutlined, CheckOutlined, DownOutlined, BulbOutlined, } from '@ant-design/icons';
// @ts-ignore - SVG
import ClawFullchainIcon from 'svg/v2/newcs_aiassistant_crashclaw_fullchain.svg';
// @ts-ignore - SVG
import ClawDispatchIcon from 'svg/v2/newcs_aiassistant_crashclaw_dispatch.svg';
// @ts-ignore - SVG
import ClawFixIcon from 'svg/v2/newcs_aiassistant_crashclaw_fix.svg';
// @ts-ignore - SVG
import ClawMonitorIcon from 'svg/v2/newcs_aiassistant_crashclaw_monitor.svg';
import { useGetState } from 'ahooks';
import { ze } from 'utils/zhEn';
// @ts-ignore - JS module
import RestHelper from 'utils/RestHelper';
// @ts-ignore - JS component
import AiChatBotInputBox from 'components/top/AiDrawer/AiChatBotInputBox';
// @ts-ignore - JS component
import AiDrawerAutoScrollBody from 'components/top/AiDrawer/AiDrawerAutoScrollBody';
// @ts-ignore - JS component
import AiChatBotBubble from 'components/top/AiDrawer/AiChatBotBubble';
import { AgUiEventType, parseAgUiEvent, extractConversationId, } from './ag-ui-protocol';
import ToolCallPanel from './ToolCallPanel';
import AttachedFilesPanel from './AttachedFilesPanel';
import HtmlPreviewPanel from './HtmlPreviewPanel';
import FileDeliveryPanel from './FileDeliveryPanel';
import ThinkingPanel from './ThinkingPanel';
import { buildCrashClawConfigUrl, getWorkspaceDisplayName, isWorkspaceOwner, } from 'components/user/CrashClawConfig/crashClawWorkspaceUtil';
import useZuStore from 'store/zustand/zustand-store';
import { dedupeToolCallItems, findPendingToolCallIndex, markAllToolCallsCompleted, markToolCallCompleted, sanitizeAssistantMarkdownText, upsertToolCallItem, upsertHtmlPreviewItem, upsertFileDeliveryItem, } from './crashClawChatUtil';
import { VENUS_LLM_MODELS, DEFAULT_LLM_MODEL } from 'components/user/CrashClawConfig/crashClawDefaults';
// @ts-ignore - SCSS module
import scss from './AiDrawer.scss';
// @ts-ignore - SVG
import CrashClawWelcomeIcon from 'svg/v2/newcs_aiassistant_crashclaw_welcome.svg';
// ============================================================
// Constants
// ============================================================
const API_PREFIX = '/redir/api/crashclaw';
/**
 * CrashClaw 子场景枚举。卡片点击后进入对应子页面，发送消息时把该值带到 chat body 的 intention 字段，
 * 后端按 intention 选择不同的工作流模板（全链路 / 自动分单 / 代码修复 / 工作流监控）。
 */
export var ClawIntention;
(function (ClawIntention) {
    ClawIntention["FULLCHAIN"] = "FULLCHAIN";
    ClawIntention["DISPATCH"] = "DISPATCH";
    ClawIntention["FIX"] = "FIX";
    ClawIntention["MONITOR"] = "MONITOR";
})(ClawIntention || (ClawIntention = {}));
/**
 * CrashClaw 各子场景的「你还可以直接问我」起手快捷问题（与 AiChatBotEx QUICK_SUGGEST_MAP 对齐）。
 *
 * 用户首次进入子场景时不一定知道怎么开口，给 3 条典型示例。
 */
const CLAW_QUICK_SUGGEST_MAP = {
    [ClawIntention.FULLCHAIN]: [
        ze('对 Top10 崩溃问题跑一次全链路分析', 'Run a full-chain analysis on Top 10 crashes'),
        ze('分析 issue 12345 的根本原因并生成报告', 'Analyze the root cause of issue 12345 and generate a report'),
        ze('为这个崩溃匹配最相似的历史问题', 'Find historical issues most similar to this crash'),
    ],
    [ClawIntention.DISPATCH]: [
        ze('把昨天新增的高优问题自动分单', 'Auto-dispatch yesterday\'s new high-priority issues'),
        ze('帮我判断这个 issue 应该派给谁', 'Help me decide who should own this issue'),
        ze('为该模块配置默认负责人规则', 'Configure default owner rules for this module'),
    ],
    [ClawIntention.FIX]: [
        ze('为这个空指针崩溃生成一份修复 diff', 'Generate a fix diff for this NPE crash'),
        ze('review 这段堆栈对应的代码风险', 'Review the code risks for this stack trace'),
        ze('把修复直接以 MR 形式提交到对应仓库', 'Submit the fix as an MR to the corresponding repo'),
    ],
    [ClawIntention.MONITOR]: [
        ze('查看最近一次失败的工作流详情', 'Show the details of the most recent failed workflow'),
        ze('对比本周与上周的工作流执行成功率', 'Compare this week\'s workflow success rate with last week'),
        ze('为连续失败 3 次的工作流配置告警', 'Configure alerts for workflows that fail 3 times in a row'),
    ],
};
const CRASHCLAW_SCENARIO_CARDS = [
    {
        intention: ClawIntention.FULLCHAIN,
        icon: ClawFullchainIcon,
        title: ze('全链路分析', 'Full Chain Analysis'),
        subtitle: ze('从崩溃到修复的完整工作流', 'End-to-end workflow from crash to fix'),
        pageTitle: ze('全链路分析，一键串起崩溃到修复', 'Full Chain Analysis: From Crash to Fix'),
        pageSubtitle: ze('请描述你想分析的崩溃问题，我将依次完成根因分析、分单、修复建议与通知', 'Describe the crash to analyze. I will run root cause analysis, dispatch, fix suggestion and notification in order.'),
    },
    {
        intention: ClawIntention.DISPATCH,
        icon: ClawDispatchIcon,
        title: ze('自动分单', 'Auto Dispatch'),
        subtitle: ze('智能派发到对应负责人', 'Smart task assignment to owners'),
        pageTitle: ze('自动分单，精准派发到对应负责人', 'Auto Dispatch to the Right Owner'),
        pageSubtitle: ze('请告诉我需要分单的崩溃 ID 或描述，我会依据代码所有权与历史处理人推荐 TAPD 负责人', 'Tell me the crash ID / description, I will recommend the TAPD owner based on code ownership and history.'),
    },
    {
        intention: ClawIntention.FIX,
        icon: ClawFixIcon,
        title: ze('代码修复', 'Code Fix'),
        subtitle: ze('生成修复代码并提交MR', 'Generate fix code and submit MR'),
        pageTitle: ze('代码修复，自动生成补丁并提交 MR', 'Code Fix: Auto-Generate Patch & Submit MR'),
        pageSubtitle: ze('请提供崩溃栈或问题描述，我会定位代码、生成修复补丁并发起 MR', 'Provide the crash stack or description. I will locate the code, generate the patch and open an MR.'),
    },
    {
        intention: ClawIntention.MONITOR,
        icon: ClawMonitorIcon,
        title: ze('工作流监控', 'Workflow Monitor'),
        subtitle: ze('查看自动化流程执行状态', 'View automation workflow status'),
        pageTitle: ze('工作流监控，掌握每条工作流执行状态', 'Workflow Monitor: Track Every Run'),
        pageSubtitle: ze('告诉我你想查看的工作流或时间范围，我会汇总每条工作流的执行情况', 'Tell me the workflow / time range to inspect. I will summarize each run.'),
    },
];
// ============================================================
// Helpers
// ============================================================
/** 将 CoPaw ChatSpec 列表转换为 AiChatBotConversationList 所需格式 */
function transformCopawSessionsToList(sessions) {
    return sessions
        .map((s) => {
        const updatedRaw = s.updated_at || s.created_at;
        const updatedMs = updatedRaw ? Date.parse(updatedRaw) : NaN;
        const updatedAt = Number.isFinite(updatedMs)
            ? Math.floor(updatedMs / 1000)
            : Math.floor(Date.now() / 1000);
        return {
            id: s.id,
            name: s.name || 'New Chat',
            updated_at: updatedAt,
        };
    })
        .sort((a, b) => b.updated_at - a.updated_at);
}
/** 工作区列表刷新后，校正当前选中项（删除/新建后避免仍指向已不存在的工作区） */
function reconcileWorkspaceSelection(list, prevUid, preferredUid) {
    if (preferredUid) {
        return preferredUid;
    }
    if (list.length === 0)
        return '';
    if (prevUid && list.some((ws) => ws.workspaceUid === prevUid)) {
        return prevUid;
    }
    const firstOnline = list.find((ws) => ws.healthStatus === 'HEALTHY');
    return firstOnline?.workspaceUid ?? list[0].workspaceUid;
}
/** StrictMode 双发防护：模块级，跨 remount 仍只 send / load 一次 */
let lastIssueAnalysisAutoSendKey = '';
let lastIssueAnalysisOpenKey = '';
/** 从 CoPaw 消息数组中提取纯文本 */
function extractTextFromCopawContent(content) {
    if (typeof content === 'string')
        return content;
    if (!Array.isArray(content))
        return '';
    return content
        .map((c) => {
        if (typeof c === 'string')
            return c;
        if (c.type === 'text' && c.text)
            return c.text;
        return '';
    })
        .join('');
}
/** 从 plugin_call 消息中提取 tool call 数据 */
function extractPluginCallData(msg) {
    if (typeof msg.content === 'string')
        return { name: 'unknown_tool' };
    for (const c of msg.content) {
        if (typeof c !== 'string' && c.type === 'data' && c.data) {
            return {
                name: c.data.name || 'unknown_tool',
                arguments: c.data.arguments,
                callId: c.data.call_id,
            };
        }
    }
    return { name: 'unknown_tool' };
}
/** 从 plugin_call_output 消息中提取输出文本 */
function extractPluginCallOutputText(msg) {
    if (typeof msg.content === 'string')
        return msg.content;
    for (const c of msg.content) {
        if (typeof c !== 'string' && c.type === 'data' && c.data?.output) {
            const raw = c.data.output;
            try {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) {
                    return parsed
                        .map((item) => (typeof item === 'string' ? item : item.text || JSON.stringify(item)))
                        .join('\n');
                }
                return typeof parsed === 'string' ? parsed : JSON.stringify(parsed, null, 2);
            }
            catch {
                return raw;
            }
        }
    }
    return '';
}
/**
 * 将 CoPaw ChatHistory 的 messages 转为本地 ChatHistoryItem[]。
 * plugin_call + 紧随其后的 plugin_call_output 配对合并为一个 toolCall 项。
 */
function copawMessagesToChatHistory(messages) {
    const result = [];
    for (let i = 0; i < messages.length; i++) {
        const msg = messages[i];
        const msgType = msg.type || 'message';
        if (msgType === 'plugin_call') {
            const callData = extractPluginCallData(msg);
            let output = '';
            // 向后查找匹配的 plugin_call_output（通常紧随其后）
            if (i + 1 < messages.length && (messages[i + 1].type === 'plugin_call_output')) {
                output = extractPluginCallOutputText(messages[i + 1]);
                i++;
            }
            result.push({
                isBotNotUser: true,
                text: '',
                toolCall: {
                    name: callData.name,
                    arguments: callData.arguments,
                    output,
                    callId: callData.callId,
                    // 历史消息一定是已经执行过的工具调用，直接标记完成
                    completed: true,
                },
            });
        }
        else if (msgType === 'plugin_call_output') {
            // 孤立的 output（前面没有 plugin_call 配对），仍渲染
            const callData = extractPluginCallData(msg);
            result.push({
                isBotNotUser: true,
                text: '',
                toolCall: {
                    name: callData.name || 'tool_output',
                    output: extractPluginCallOutputText(msg),
                    completed: true,
                },
            });
        }
        else {
            const text = extractTextFromCopawContent(msg.content);
            if (text.trim()) {
                result.push({
                    isBotNotUser: msg.role !== 'user',
                    text: msg.role !== 'user' ? sanitizeAssistantMarkdownText(text) : text,
                });
            }
        }
    }
    return dedupeToolCallItems(result);
}
// ToolCallPanel 已抽取为共享组件 ./ToolCallPanel.tsx，CrashClaw 和 AI Agent 模式共用
// ============================================================
// Component
// ============================================================
const CrashClawChatPanel = ({ onConversationListChange, onCurrentChatIdChange, selectChatId, selectChatSeq, newSessionSignal, refreshHistorySignal, onActiveIntentionChange, backToHomeSignal, onSelectedWorkspaceUidChange, panelActive = false, controlledSelectedWorkspaceUid, controlledWorkspaces, controlledWorkspacesLoaded, hideInternalPicker = false, externalLeftElement, restoreChatId, onRestoreChatIdConsumed, issueAnalysisWorkspaceUid, issueAnalysisSessionId, issueAnalysisAutoSendText, issueAnalysisAppId, issueAnalysisIssueId, issueAnalysisSeq = 0, onIssueAnalysisConsumed, }) => {
    // --- Workspace 状态 ---
    const [workspaces, setWorkspaces] = useState([]);
    const [selectedWorkspaceUid, setSelectedWorkspaceUid] = useState('');
    const [loadingWorkspaces, setLoadingWorkspaces] = useState(true);
    // 父组件受控模式：仅在选择 CrashClaw 工作区时同步 UID，避免在 CrashSight AI 下清空已选工作区
    useEffect(() => {
        if (controlledSelectedWorkspaceUid !== undefined && controlledSelectedWorkspaceUid !== '') {
            setSelectedWorkspaceUid(controlledSelectedWorkspaceUid);
        }
    }, [controlledSelectedWorkspaceUid]);
    // 父组件受控模式：controlledWorkspaces 提供时直接使用，跳过内部拉取
    useEffect(() => {
        if (controlledWorkspaces === undefined)
            return;
        if (controlledWorkspacesLoaded === false) {
            setLoadingWorkspaces(true);
            return;
        }
        setWorkspaces(controlledWorkspaces);
        setLoadingWorkspaces(false);
        workspacesLoadedOnceRef.current = true;
    }, [controlledWorkspaces, controlledWorkspacesLoaded]);
    // --- 聊天状态（与 AI Agent 完全独立）---
    const [chatHistoryList, setChatHistoryList, getChatHistoryList] = useGetState([]);
    const [currentAnswerRandomId, setCurrentAnswerRandomId, getCurrentAnswerRandomId] = useGetState('');
    const [currentAnswerInfo, setCurrentAnswerInfo, getCurrentAnswerInfo] = useGetState(null);
    const [currentThinkingInfo, setCurrentThinkingInfo, getCurrentThinkingInfo] = useGetState(null);
    const [isAnswerOutputting, setIsAnswerOutputting, getIsAnswerOutputting] = useGetState(false);
    const [abortController, setAbortController] = useState(null);
    // CoPaw 侧的 session_id，同一 workspace 下保持不变以支持多轮对话
    const [clawSessionId, setClawSessionId, getClawSessionId] = useGetState('');
    // --- CoPaw 会话历史状态 ---
    const [copawSessions, setCopawSessions] = useState([]);
    const [copawSessionsLoading, setCopawSessionsLoading] = useState(false);
    const [currentChatId, setCurrentChatId] = useState('');
    const [loadingSessionMessages, setLoadingSessionMessages] = useState(false);
    // --- 子场景状态 ---
    // 用户点击场景卡片后进入对应的"子页面"：展示场景大标题/副标题 + 输入框，
    // 发送消息时把 intention 带到 chat body，由后端按场景挑选不同的工作流模板。
    const [clawActiveIntention, setClawActiveIntention] = useState(null);
    // 子场景变化时通知父组件，父组件据此在 header 显示返回按钮
    useEffect(() => {
        if (onActiveIntentionChange)
            onActiveIntentionChange(clawActiveIntention);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clawActiveIntention]);
    // 响应父组件 header "返回" 按钮点击：每次 signal 变化都退出当前子场景
    useEffect(() => {
        if (backToHomeSignal === undefined)
            return;
        setClawActiveIntention(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [backToHomeSignal]);
    // 模型切换：从后端获取可选模型列表，兜底使用前端白名单
    const [modelOptions, setModelOptions] = useState(VENUS_LLM_MODELS);
    const [selectedModel, setSelectedModel] = useState(DEFAULT_LLM_MODEL);
    const fetchLlmModelsRef = useRef(false);
    useEffect(() => {
        if (fetchLlmModelsRef.current)
            return;
        fetchLlmModelsRef.current = true;
        (async () => {
            try {
                const rsp = await RestHelper.post(`${API_PREFIX}/listLlmModels`);
                if (rsp?.json?.data?.length)
                    setModelOptions(rsp.json.data);
            }
            catch { /* fallback to VENUS_LLM_MODELS */ }
        })();
    }, []);
    // 切换工作区时查询该工作区当前使用的模型，让胶囊显示正确的初始值
    useEffect(() => {
        if (!selectedWorkspaceUid)
            return;
        (async () => {
            try {
                const rsp = await RestHelper.mustPost(`${API_PREFIX}/getWorkspaceModel`, {
                    workspaceUid: selectedWorkspaceUid,
                });
                if (rsp?.json?.data?.model) {
                    setSelectedModel(rsp.json.data.model);
                }
            }
            catch { /* 查询失败保持默认值 */ }
        })();
    }, [selectedWorkspaceUid]);
    /** 用户在胶囊中选择模型：持久化到 Agent + 触发 config_sync，不传请求级模型 */
    async function handleModelChange(model) {
        setSelectedModel(model);
        if (!selectedWorkspaceUid)
            return;
        try {
            await RestHelper.mustPost(`${API_PREFIX}/updateWorkspaceModel`, {
                workspaceUid: selectedWorkspaceUid,
                model,
            });
            message.success(ze('模型已切换', 'Model switched'));
        }
        catch {
            message.error(ze('模型切换失败，请重试', 'Model switch failed, please retry'));
        }
    }
    // 记录上一次处理的 selectChatSeq（每次点击会话都会递增，含重复点击同一会话）
    const lastSelectChatSeqRef = useRef(0);
    /** 会话消息加载代数：新请求 supersede 旧请求，避免切换中点击被静默丢弃 */
    const loadSessionGenerationRef = useRef(0);
    // 记录上一次的 newSessionSignal
    const lastNewSessionSignalRef = useRef(0);
    const lastRefreshHistorySignalRef = useRef(0);
    const fetchCopawSessionsRetryTimersRef = useRef([]);
    /** RUN_FINISHED 后回填 tool call output 的重试定时器 */
    const backfillToolCallTimersRef = useRef([]);
    /** 用户主动「新建会话」后跳过自动加载最近会话 */
    const skipAutoLoadRecentRef = useRef(false);
    /** 切换工作区后待自动打开最近会话（仅消费一次） */
    const pendingAutoLoadWorkspaceRef = useRef(null);
    /** 工作区列表是否已成功拉取过（用于区分首屏 loading 与静默刷新） */
    const workspacesLoadedOnceRef = useRef(false);
    /** 最近一次实际拉取过会话列表的工作区（用于离线恢复后补拉，避免与首次选择重复拉取） */
    const fetchedSessionsWorkspaceRef = useRef('');
    /** workspaces 的镜像 ref：给 useCallback 内的离线守卫读最新列表，避免把 workspaces 加进依赖导致下游回调频繁重建 */
    const workspacesRef = useRef([]);
    const prevDrawerVisibleRef = useRef(false);
    const prevPanelActiveRef = useRef(false);
    const workspacesRefreshTrigger = useZuStore((s) => s.aiDrawer.crashClawWorkspacesRefreshTrigger);
    const refreshCrashClawWorkspaces = useZuStore((s) => s.aiDrawer.refreshCrashClawWorkspaces);
    const drawerVisible = useZuStore((s) => s.aiDrawer.visible);
    const bumpIssueAnalysisRefresh = useZuStore((s) => s.aiDrawer.bumpIssueAnalysisRefresh);
    const hasWorkspaces = workspaces.length > 0;
    const selectedWorkspace = workspaces.find(ws => ws.workspaceUid === selectedWorkspaceUid);
    // 镜像 ref 在每次渲染时同步，供 useCallback 内的离线守卫读取最新列表
    workspacesRef.current = workspaces;
    /** 是否存在至少一个在线（HEALTHY）工作区。用于控制发送按钮禁用 + 离线 Alert 横幅展示 */
    const hasOnlineWorkspace = workspaces.some(ws => ws.healthStatus === 'HEALTHY');
    /** 当前选中工作区是否离线（healthStatus !== 'HEALTHY'）。纯派生态，不新增持久状态 */
    const selectedWorkspaceOffline = !!selectedWorkspace && selectedWorkspace.healthStatus !== 'HEALTHY';
    const isInChat = hasWorkspaces && selectedWorkspaceUid && (chatHistoryList.length > 0 || currentAnswerInfo);
    // 合并历史消息和当前正在输出的回复
    const finalChatHistoryList = useMemo(() => {
        return [...chatHistoryList, currentThinkingInfo, currentAnswerInfo].filter((x) => !!x);
    }, [chatHistoryList, currentThinkingInfo, currentAnswerInfo]);
    // 转换后的会话列表（供父组件传给 AiChatBotConversationList）
    const conversationList = useMemo(() => transformCopawSessionsToList(copawSessions), [copawSessions]);
    // ============================================================
    // 通知父组件
    // ============================================================
    useEffect(() => {
        onConversationListChange?.(conversationList);
    }, [conversationList]);
    useEffect(() => {
        onCurrentChatIdChange?.(currentChatId);
    }, [currentChatId]);
    // ============================================================
    // 响应父组件的选择/新建会话指令
    // ============================================================
    useEffect(() => {
        if (!selectChatId || !selectChatSeq)
            return;
        if (selectChatSeq === lastSelectChatSeqRef.current)
            return;
        lastSelectChatSeqRef.current = selectChatSeq;
        loadSessionMessages(selectChatId);
    }, [selectChatId, selectChatSeq]);
    useEffect(() => {
        if (newSessionSignal != null && newSessionSignal !== lastNewSessionSignalRef.current) {
            lastNewSessionSignalRef.current = newSessionSignal;
            startNewSession();
        }
    }, [newSessionSignal]);
    // ============================================================
    // 数据加载
    // ============================================================
    const fetchWorkspaces = useCallback(async (silent) => {
        // 受控模式：父组件提供工作区列表，跳过内部拉取
        if (controlledWorkspaces !== undefined) {
            setWorkspaces(controlledWorkspaces);
            setLoadingWorkspaces(false);
            workspacesLoadedOnceRef.current = true;
            return;
        }
        const shouldSilent = silent ?? workspacesLoadedOnceRef.current;
        if (!shouldSilent) {
            setLoadingWorkspaces(true);
        }
        try {
            const rsp = await RestHelper.mustPost(`${API_PREFIX}/listWorkspaces`);
            const list = rsp.json.data || [];
            setWorkspaces(list);
            setSelectedWorkspaceUid((prev) => reconcileWorkspaceSelection(list, prev, issueAnalysisWorkspaceUid));
            workspacesLoadedOnceRef.current = true;
        }
        catch (e) {
            console.error('[CrashClawChat] Failed to fetch workspaces', e);
        }
        finally {
            if (!shouldSilent) {
                setLoadingWorkspaces(false);
            }
        }
    }, [controlledWorkspaces, issueAnalysisWorkspaceUid]);
    const isControlledWorkspaces = controlledWorkspaces !== undefined;
    useEffect(() => {
        if (!isControlledWorkspaces) {
            fetchWorkspaces(false);
        }
    }, [fetchWorkspaces, isControlledWorkspaces]);
    useEffect(() => {
        if (workspacesRefreshTrigger > 0 && !isControlledWorkspaces) {
            fetchWorkspaces(true);
        }
    }, [workspacesRefreshTrigger, fetchWorkspaces, isControlledWorkspaces]);
    // 切换 workspace 时重置聊天状态 + 拉取会话历史（默认进入最近一条会话）
    useEffect(() => {
        loadSessionGenerationRef.current += 1;
        setChatHistoryList([]);
        setCurrentAnswerInfo(null);
        setCurrentThinkingInfo(null);
        setIsAnswerOutputting(false);
        setCopawSessions([]);
        setCurrentChatId('');
        setClawSessionId(`claw-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
        skipAutoLoadRecentRef.current = false;
        pendingAutoLoadWorkspaceRef.current = selectedWorkspaceUid;
        if (issueAnalysisWorkspaceUid && selectedWorkspaceUid === issueAnalysisWorkspaceUid) {
            skipAutoLoadRecentRef.current = !!(issueAnalysisSessionId || issueAnalysisAutoSendText);
            if (issueAnalysisAutoSendText) {
                pendingAutoLoadWorkspaceRef.current = null;
            }
        }
        if (selectedWorkspaceUid && !selectedWorkspaceOffline) {
            setCopawSessionsLoading(true);
            fetchedSessionsWorkspaceRef.current = selectedWorkspaceUid;
            fetchCopawSessions();
            checkMcpRestartStatus();
        }
        else {
            // 选中工作区离线（或暂未选中）：跳过会话列表 / MCP 状态拉取，
            // 避免后端对离线 Bridge 返回 400 触发全局错误弹窗（见 20261027 cnprod 事故）。
            // 离线提示由渲染区的行内提示条承担。
            fetchedSessionsWorkspaceRef.current = '';
            setCopawSessionsLoading(false);
            setCopawSessions([]);
        }
    }, [selectedWorkspaceUid]);
    useEffect(() => {
        onSelectedWorkspaceUidChange?.(selectedWorkspaceUid);
    }, [selectedWorkspaceUid, onSelectedWorkspaceUidChange]);
    // MCP 懒重启失败事件的去重 key："{workspaceUid}:{timestampMs}"。
    // 同一条 failed 事件只弹一次 toast；避免切换会话 / 刷新时反复弹同一条。
    const toastedMcpFailedKeyRef = useRef('');
    /**
     * 查询当前 Workspace 最近一次 MCP 懒重启事件，发现 failed 且新鲜（< 60s）就弹 toast。
     *
     * 这个函数在两个时机调用：
     *   1. Workspace 切换 / 聊天抽屉打开时（useEffect 里）
     *   2. 每次聊天完成时（chat 的 stream 结束钩子里）
     *
     * 为什么不靠 WS 推到前端：前端 CrashClaw 聊天抽屉没有持久 WS / SSE 通道，
     * 只有聊天流 SSE 在活跃聊天时短暂存在。轮询 + 去重是最简单的实现。
     */
    const checkMcpRestartStatus = useCallback(async () => {
        if (!selectedWorkspaceUid)
            return;
        try {
            const rsp = await RestHelper.mustPost(`${API_PREFIX}/getWorkspaceMcpStatus`, {
                workspaceUid: selectedWorkspaceUid,
            });
            const d = rsp.json.data;
            if (!d || !d.hasEvent)
                return;
            if (d.eventType !== 'failed')
                return;
            if (typeof d.ageMs === 'number' && d.ageMs > 60 * 1000)
                return;
            const key = `${selectedWorkspaceUid}:${d.timestampMs || 0}`;
            if (toastedMcpFailedKeyRef.current === key)
                return;
            toastedMcpFailedKeyRef.current = key;
            message.error({
                content: ze(`MCP 配置更新失败：${d.error || '未知错误'}。请在开发机上执行 bash ~/.crashclaw-bridge/restart-hermes.sh 手动重启 Hermes gateway。`, `MCP config update failed: ${d.error || 'unknown error'}. Please run bash ~/.crashclaw-bridge/restart-hermes.sh on your dev machine to manually restart Hermes gateway.`),
                duration: 10,
            });
        }
        catch (e) {
            // 失败静默：这是可选的状态查询，不影响主流程
            console.warn('[CrashClawChat] checkMcpRestartStatus failed', e);
        }
    }, [selectedWorkspaceUid]);
    /** 拉取 CoPaw 会话历史列表 */
    const fetchCopawSessions = useCallback(async () => {
        if (!selectedWorkspaceUid)
            return;
        // 离线守卫：选中工作区非 HEALTHY 时不调 listCopawSessions（后端会返回 400 BridgeOffline），
        // 由行内离线提示条承担提示。这里用 workspacesRef 读最新列表，避免把 workspaces 加进依赖。
        const selectedWs = workspacesRef.current.find((ws) => ws.workspaceUid === selectedWorkspaceUid);
        if (selectedWs && selectedWs.healthStatus !== 'HEALTHY') {
            setCopawSessionsLoading(false);
            setCopawSessions([]);
            return;
        }
        setCopawSessionsLoading(true);
        try {
            const rsp = await RestHelper.mustPost(`${API_PREFIX}/listCopawSessions`, {
                workspaceUid: selectedWorkspaceUid,
            }, { silentErrorCodes: ['BridgeOffline'] });
            const sessions = Array.isArray(rsp.json.data) ? rsp.json.data : [];
            setCopawSessions(sessions);
        }
        catch (e) {
            if (e?.errorCode === 'BridgeOffline') {
                // Bridge 离线：静默，由面板行内离线提示条承担提示，不打 warning toast
                return;
            }
            console.error('[CrashClawChat] Failed to fetch CoPaw sessions', e);
            message.warning(ze('历史会话加载失败，请确认 Bridge 在线后重试', 'Failed to load chat history; ensure Bridge is online'));
        }
        finally {
            setCopawSessionsLoading(false);
        }
    }, [selectedWorkspaceUid]);
    // 离线恢复：选中工作区从非 HEALTHY 回到 HEALTHY（如用户点提示条「重试」刷新 listWorkspaces
    // 后 Bridge 已重连），补拉会话列表与 MCP 状态。首次选中在线工作区时
    // fetchedSessionsWorkspaceRef 已在 selectedWorkspaceUid effect 记录，不会重复拉取。
    // 注意：必须声明在 fetchCopawSessions / checkMcpRestartStatus 之后（deps 数组在渲染期即求值）。
    useEffect(() => {
        if (!selectedWorkspaceUid)
            return;
        if (selectedWorkspace?.healthStatus !== 'HEALTHY')
            return;
        if (fetchedSessionsWorkspaceRef.current === selectedWorkspaceUid)
            return;
        fetchedSessionsWorkspaceRef.current = selectedWorkspaceUid;
        setCopawSessionsLoading(true);
        fetchCopawSessions();
        checkMcpRestartStatus();
    }, [selectedWorkspace, selectedWorkspaceUid, fetchCopawSessions, checkMcpRestartStatus]);
    /** 标记待自动打开最近会话并拉取列表（抽屉打开 / Tab 激活等场景复用） */
    const requestAutoLoadRecentSession = useCallback(() => {
        if (!drawerVisible || !selectedWorkspaceUid)
            return;
        if (issueAnalysisSessionId || issueAnalysisAutoSendText)
            return;
        if (skipAutoLoadRecentRef.current)
            return;
        if (currentChatId && chatHistoryList.length > 0)
            return;
        if (chatHistoryList.length > 0 || currentAnswerInfo)
            return;
        pendingAutoLoadWorkspaceRef.current = selectedWorkspaceUid;
        fetchCopawSessions();
    }, [
        drawerVisible,
        selectedWorkspaceUid,
        currentChatId,
        chatHistoryList.length,
        currentAnswerInfo,
        fetchCopawSessions,
        issueAnalysisSessionId,
        issueAnalysisAutoSendText,
    ]);
    useEffect(() => {
        const justOpened = drawerVisible && !prevDrawerVisibleRef.current;
        prevDrawerVisibleRef.current = drawerVisible;
        if (!drawerVisible) {
            if (!(issueAnalysisSessionId || issueAnalysisAutoSendText)) {
                skipAutoLoadRecentRef.current = false;
            }
            return;
        }
        if (!isControlledWorkspaces) {
            fetchWorkspaces(true);
        }
        if (justOpened) {
            requestAutoLoadRecentSession();
        }
    }, [drawerVisible, fetchWorkspaces, requestAutoLoadRecentSession, isControlledWorkspaces]);
    /** 从 AI Agent 等 Tab 切到 CrashClaw 时，补一次最近会话自动加载 */
    useEffect(() => {
        const justActivated = panelActive && !prevPanelActiveRef.current;
        prevPanelActiveRef.current = panelActive;
        if (!justActivated)
            return;
        requestAutoLoadRecentSession();
    }, [panelActive, requestAutoLoadRecentSession]);
    /** 工作区列表就绪后，若 CrashClaw Tab 已可见且仍无会话，再尝试一次 */
    useEffect(() => {
        if (!panelActive || !drawerVisible || !selectedWorkspaceUid)
            return;
        requestAutoLoadRecentSession();
    }, [panelActive, drawerVisible, selectedWorkspaceUid, requestAutoLoadRecentSession]);
    /**
     * Hermes 在 stream 结束后才 commit state.db，立即拉列表常会漏掉刚结束的会话。
     * 因此在 0 / 800ms / 2s 各拉一次，提高侧边栏与 Hermes 本地列表的一致性。
     */
    const scheduleFetchCopawSessions = useCallback(() => {
        fetchCopawSessionsRetryTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
        fetchCopawSessionsRetryTimersRef.current = [];
        [0, 800, 2000].forEach((delayMs) => {
            const timerId = window.setTimeout(() => {
                fetchCopawSessions();
            }, delayMs);
            fetchCopawSessionsRetryTimersRef.current.push(timerId);
        });
    }, [fetchCopawSessions]);
    /**
     * RUN_FINISHED 后从历史接口回填 tool call 的 output / arguments。
     *
     * Hermes / CoPaw 的实时 SSE 流出于协议限制可能不下发工具的 input/output
     * （Hermes 只通过 hermes.tool.progress 告知 tool name；CoPaw 在 output 为空时不发 END），
     * 但会话结束后历史接口 getCopawSessionMessages 携带完整 plugin_call + plugin_call_output。
     * 这里按 callId 匹配，只填充本地缺失的字段，不触碰文本 / 思考消息，
     * 避免覆盖刚流式输出的内容；本地已无缺 output 的 tool call 时直接跳过，省接口。
     */
    const backfillToolCallOutputs = useCallback(async () => {
        if (!selectedWorkspaceUid || !currentChatId)
            return;
        const localList = getChatHistoryList();
        const hasPending = localList.some((item) => item.toolCall && !item.toolCall.output);
        if (!hasPending)
            return;
        const generation = loadSessionGenerationRef.current;
        try {
            const rsp = await RestHelper.mustPost(`${API_PREFIX}/getCopawSessionMessages`, {
                workspaceUid: selectedWorkspaceUid,
                chatId: currentChatId,
            }, { silentErrorCodes: ['BridgeOffline'] });
            // 期间用户切换了会话则放弃此次回填
            if (generation !== loadSessionGenerationRef.current)
                return;
            const history = rsp.json.data;
            if (!history || !Array.isArray(history.messages))
                return;
            const historyItems = copawMessagesToChatHistory(history.messages);
            const historyToolByCallId = new Map();
            for (const item of historyItems) {
                if (item.toolCall?.callId) {
                    historyToolByCallId.set(item.toolCall.callId, item.toolCall);
                }
            }
            const latest = getChatHistoryList();
            let changed = false;
            const merged = latest.map((item) => {
                if (!item.toolCall || !item.toolCall.callId)
                    return item;
                const hist = historyToolByCallId.get(item.toolCall.callId);
                if (!hist)
                    return item;
                const needOutput = !item.toolCall.output && !!hist.output;
                const needArgs = !item.toolCall.arguments && !!hist.arguments;
                if (!needOutput && !needArgs)
                    return item;
                changed = true;
                return {
                    ...item,
                    toolCall: {
                        ...item.toolCall,
                        output: needOutput ? hist.output : item.toolCall.output,
                        arguments: needArgs ? hist.arguments : item.toolCall.arguments,
                        completed: true,
                    },
                };
            });
            if (changed) {
                setChatHistoryList(merged);
            }
        }
        catch (e) {
            // 静默失败：回填是增强体验，不应影响主流程；BridgeOffline 由行内提示条承担
            if (e?.errorCode !== 'BridgeOffline') {
                console.warn('[CrashClawChat] backfillToolCallOutputs failed', e);
            }
        }
    }, [selectedWorkspaceUid, currentChatId, getChatHistoryList, setChatHistoryList]);
    /**
     * 与 scheduleFetchCopawSessions 同样的重试节奏：0 / 800ms / 2s 各回填一次。
     * Hermes 在 stream 结束后才 commit state.db，立即拉常漏掉，重试提高命中率。
     */
    const scheduleBackfillToolCallOutputs = useCallback(() => {
        backfillToolCallTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
        backfillToolCallTimersRef.current = [];
        [0, 800, 2000].forEach((delayMs) => {
            const timerId = window.setTimeout(() => {
                backfillToolCallOutputs();
            }, delayMs);
            backfillToolCallTimersRef.current.push(timerId);
        });
    }, [backfillToolCallOutputs]);
    useEffect(() => () => {
        fetchCopawSessionsRetryTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
        backfillToolCallTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    }, []);
    useEffect(() => {
        if (refreshHistorySignal != null && refreshHistorySignal !== lastRefreshHistorySignalRef.current) {
            lastRefreshHistorySignalRef.current = refreshHistorySignal;
            if (selectedWorkspaceUid
                && !skipAutoLoadRecentRef.current
                && !currentChatId
                && chatHistoryList.length === 0) {
                pendingAutoLoadWorkspaceRef.current = selectedWorkspaceUid;
            }
            fetchCopawSessions();
        }
    }, [refreshHistorySignal, fetchCopawSessions, selectedWorkspaceUid, currentChatId, chatHistoryList.length]);
    /** 加载指定会话的消息历史并切换到该会话 */
    async function loadSessionMessages(chatId, sessionsSource) {
        if (!selectedWorkspaceUid || !chatId)
            return;
        // 离线守卫：选中工作区非 HEALTHY 时不调 getCopawSessionMessages，提示交给行内离线提示条
        const selectedWs = workspacesRef.current.find((ws) => ws.workspaceUid === selectedWorkspaceUid);
        if (selectedWs && selectedWs.healthStatus !== 'HEALTHY') {
            return;
        }
        const generation = ++loadSessionGenerationRef.current;
        // 停止当前对话（含流式输出中的 SSE）
        if (getIsAnswerOutputting()) {
            stopCurrentChat();
        }
        setLoadingSessionMessages(true);
        try {
            const rsp = await RestHelper.mustPost(`${API_PREFIX}/getCopawSessionMessages`, {
                workspaceUid: selectedWorkspaceUid,
                chatId,
            }, { silentErrorCodes: ['BridgeOffline'] });
            if (generation !== loadSessionGenerationRef.current) {
                return;
            }
            const history = rsp.json.data;
            if (history && history.messages) {
                const items = copawMessagesToChatHistory(history.messages);
                setChatHistoryList(items);
                setCurrentAnswerInfo(null);
                setCurrentThinkingInfo(null);
            }
            else {
                setChatHistoryList([]);
                setCurrentAnswerInfo(null);
                setCurrentThinkingInfo(null);
            }
            const sessions = sessionsSource ?? copawSessions;
            const session = sessions.find((s) => s.id === chatId || s.session_id === chatId);
            if (session) {
                setClawSessionId(session.session_id || session.id);
            }
            else {
                setClawSessionId(chatId);
            }
            setCurrentChatId(chatId);
        }
        catch (e) {
            if (generation !== loadSessionGenerationRef.current) {
                return;
            }
            if (e?.errorCode === 'BridgeOffline') {
                // Bridge 离线：静默，由面板行内离线提示条承担提示，不打错误 toast
                return;
            }
            console.error('[CrashClawChat] Failed to load session messages', e);
            message.error(ze('加载会话历史失败', 'Failed to load chat history'));
        }
        finally {
            if (generation === loadSessionGenerationRef.current) {
                setLoadingSessionMessages(false);
            }
        }
    }
    /** 切换工作区后默认打开最近一条历史会话（用户主动「新建会话」时跳过） */
    useEffect(() => {
        if (!selectedWorkspaceUid)
            return;
        if (copawSessionsLoading)
            return;
        if (pendingAutoLoadWorkspaceRef.current !== selectedWorkspaceUid)
            return;
        if (skipAutoLoadRecentRef.current) {
            pendingAutoLoadWorkspaceRef.current = null;
            return;
        }
        if (issueAnalysisAutoSendText && issueAnalysisWorkspaceUid === selectedWorkspaceUid) {
            pendingAutoLoadWorkspaceRef.current = null;
            return;
        }
        if (issueAnalysisSessionId && issueAnalysisWorkspaceUid === selectedWorkspaceUid) {
            pendingAutoLoadWorkspaceRef.current = null;
            return;
        }
        if (currentChatId) {
            pendingAutoLoadWorkspaceRef.current = null;
            return;
        }
        if (copawSessions.length === 0) {
            pendingAutoLoadWorkspaceRef.current = null;
            return;
        }
        // 优先恢复 localStorage 中保存的会话（打开抽屉时由父组件传入）
        if (restoreChatId) {
            pendingAutoLoadWorkspaceRef.current = null;
            loadSessionMessages(restoreChatId, copawSessions);
            onRestoreChatIdConsumed?.();
            return;
        }
        const latest = transformCopawSessionsToList(copawSessions)[0];
        if (latest?.id) {
            pendingAutoLoadWorkspaceRef.current = null;
            loadSessionMessages(latest.id, copawSessions);
        }
    }, [copawSessions, selectedWorkspaceUid, currentChatId, copawSessionsLoading, restoreChatId, issueAnalysisSessionId, issueAnalysisAutoSendText, issueAnalysisWorkspaceUid]);
    /** 开始新会话：重置聊天状态，生成新 session_id，并退出当前子场景 */
    function startNewSession() {
        if (getIsAnswerOutputting()) {
            stopCurrentChat();
        }
        loadSessionGenerationRef.current += 1;
        setLoadingSessionMessages(false);
        skipAutoLoadRecentRef.current = true;
        pendingAutoLoadWorkspaceRef.current = null;
        setChatHistoryList([]);
        setCurrentAnswerInfo(null);
        setCurrentThinkingInfo(null);
        setCurrentChatId('');
        const nextId = `claw-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        setClawSessionId(nextId);
        setClawActiveIntention(null);
        return nextId;
    }
    useEffect(() => {
        if (!issueAnalysisSeq)
            return;
        if (!issueAnalysisWorkspaceUid)
            return;
        if (!panelActive)
            return;
        if (selectedWorkspaceUid !== issueAnalysisWorkspaceUid)
            return;
        if (loadingWorkspaces)
            return;
        if (issueAnalysisSessionId) {
            skipAutoLoadRecentRef.current = true;
            pendingAutoLoadWorkspaceRef.current = null;
            const openKey = `${issueAnalysisSeq}:${issueAnalysisSessionId}`;
            if (lastIssueAnalysisOpenKey === openKey) {
                onIssueAnalysisConsumed?.();
                return;
            }
            // 等会话列表回来再按 id / session_id 对齐；未入列表时仍用映射 id 兜底 GET
            if (copawSessionsLoading)
                return;
            const matched = copawSessions.find((s) => s.id === issueAnalysisSessionId || s.session_id === issueAnalysisSessionId);
            lastIssueAnalysisOpenKey = openKey;
            loadSessionMessages(matched?.id || issueAnalysisSessionId, copawSessions);
            onIssueAnalysisConsumed?.();
            return;
        }
        if (!issueAnalysisAutoSendText)
            return;
        if (!selectedWorkspace)
            return;
        const key = `${issueAnalysisSeq}:${issueAnalysisWorkspaceUid}:${issueAnalysisIssueId || ''}`;
        if (lastIssueAnalysisAutoSendKey === key) {
            onIssueAnalysisConsumed?.();
            return;
        }
        lastIssueAnalysisAutoSendKey = key;
        skipAutoLoadRecentRef.current = true;
        pendingAutoLoadWorkspaceRef.current = null;
        const newSid = startNewSession();
        void sendMessage(issueAnalysisAutoSendText, undefined, undefined, {
            sessionId: newSid,
            appId: issueAnalysisAppId,
            issueId: issueAnalysisIssueId,
        });
        onIssueAnalysisConsumed?.();
        // sendMessage / startNewSession 闭包随渲染更新；用 seq 保证只触发一次
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        issueAnalysisSeq,
        issueAnalysisWorkspaceUid,
        issueAnalysisSessionId,
        issueAnalysisAutoSendText,
        issueAnalysisAppId,
        issueAnalysisIssueId,
        panelActive,
        selectedWorkspaceUid,
        selectedWorkspace,
        loadingWorkspaces,
        copawSessionsLoading,
        copawSessions,
    ]);
    // ============================================================
    // 聊天逻辑
    // ============================================================
    function stopCurrentChat() {
        if (abortController) {
            abortController.abort();
            setAbortController(null);
        }
        setCurrentAnswerInfo(null);
        setCurrentThinkingInfo(null);
        setIsAnswerOutputting(false);
        setCurrentAnswerRandomId('');
    }
    /**
     * 将当前正在流式输出的文本回复（currentAnswerInfo）提交到 chatHistoryList，
     * 然后清空 currentAnswerInfo。用于在 tool call 事件到来时"截断"文本段。
     */
    function commitCurrentAnswer() {
        const answer = getCurrentAnswerInfo();
        if (answer && answer.text.trim()) {
            setChatHistoryList([...getChatHistoryList(), {
                    ...answer,
                    text: sanitizeAssistantMarkdownText(answer.text),
                }]);
        }
        setCurrentAnswerInfo(null);
    }
    function commitCurrentThinking() {
        const thinkingItem = getCurrentThinkingInfo();
        if (thinkingItem?.thinking?.text?.trim()) {
            setChatHistoryList([...getChatHistoryList(), {
                    ...thinkingItem,
                    thinking: { ...thinkingItem.thinking, isStreaming: false },
                }]);
        }
        setCurrentThinkingInfo(null);
    }
    function onReceiveSSEMessage(randomId, e) {
        if (!e.data)
            return;
        if (getCurrentAnswerRandomId() !== randomId)
            return;
        const agEvent = parseAgUiEvent(e.data);
        if (!agEvent)
            return;
        switch (agEvent.type) {
            case AgUiEventType.RUN_STARTED: {
                const hermesSessionId = extractConversationId(agEvent);
                if (hermesSessionId && !hermesSessionId.startsWith('claw-')) {
                    setClawSessionId(hermesSessionId);
                    setCurrentChatId(hermesSessionId);
                }
                break;
            }
            case AgUiEventType.TEXT_MESSAGE_START: {
                setCurrentAnswerInfo(getCurrentAnswerInfo() || { isBotNotUser: true, text: '', isLoading: true });
                break;
            }
            case AgUiEventType.TEXT_MESSAGE_CONTENT: {
                const contentEvent = agEvent;
                const answerInfo = {
                    ...(getCurrentAnswerInfo() || { isBotNotUser: true, text: '', isLoading: true }),
                };
                answerInfo.text += contentEvent.delta || '';
                answerInfo.isLoading = false;
                setCurrentAnswerInfo(answerInfo);
                break;
            }
            case AgUiEventType.TEXT_MESSAGE_END: {
                commitCurrentAnswer();
                break;
            }
            case AgUiEventType.THINKING_TEXT_MESSAGE_START: {
                commitCurrentAnswer();
                setCurrentThinkingInfo({
                    isBotNotUser: true,
                    text: '',
                    thinking: { text: '', isStreaming: true },
                });
                break;
            }
            case AgUiEventType.THINKING_TEXT_MESSAGE_CONTENT: {
                const thinkEvent = agEvent;
                const delta = thinkEvent.delta || '';
                if (!delta)
                    break;
                const current = getCurrentThinkingInfo();
                const prevText = current?.thinking?.text || '';
                setCurrentThinkingInfo({
                    isBotNotUser: true,
                    text: '',
                    thinking: { text: prevText + delta, isStreaming: true },
                });
                break;
            }
            case AgUiEventType.THINKING_TEXT_MESSAGE_END: {
                commitCurrentThinking();
                break;
            }
            case AgUiEventType.TOOL_CALL_START: {
                const tcEvent = agEvent;
                commitCurrentAnswer();
                commitCurrentThinking();
                setChatHistoryList(upsertToolCallItem(getChatHistoryList(), {
                    name: tcEvent.toolCallName || 'tool',
                    callId: tcEvent.toolCallId,
                    arguments: tcEvent.rawEvent?.arguments,
                }));
                break;
            }
            case AgUiEventType.TOOL_CALL_ARGS: {
                const argsEvent = agEvent;
                const delta = argsEvent.delta || '';
                if (!delta)
                    break;
                const list = getChatHistoryList();
                for (let i = list.length - 1; i >= 0; i--) {
                    const tc = list[i].toolCall;
                    if (tc && (!argsEvent.toolCallId || tc.callId === argsEvent.toolCallId)) {
                        const updated = [...list];
                        updated[i] = {
                            ...updated[i],
                            toolCall: {
                                ...tc,
                                arguments: (tc.arguments || '') + delta,
                            },
                        };
                        setChatHistoryList(updated);
                        break;
                    }
                }
                break;
            }
            case AgUiEventType.TOOL_CALL_END: {
                const tcEnd = agEvent;
                commitCurrentAnswer();
                commitCurrentThinking();
                const output = tcEnd.rawEvent?.output;
                if (tcEnd.toolCallId) {
                    const list = getChatHistoryList();
                    if (findPendingToolCallIndex(list, tcEnd.toolCallId) < 0) {
                        // 后端可能漏发 START（罕见），补一条记录并直接标记完成
                        setChatHistoryList(upsertToolCallItem(list, {
                            name: tcEnd.toolCallName || 'tool',
                            callId: tcEnd.toolCallId,
                            output: output || undefined,
                            completed: true,
                        }));
                    }
                    else {
                        // 关键：无论 output 是否存在都要标记完成。
                        // Hermes 在 label == tool_name 时下发 output=None，
                        // CoPaw 在 output 为空时根本不发 TOOL_CALL_END —— 这里覆盖 Hermes 那条路径，
                        // CoPaw 的兜底由 RUN_FINISHED 的 markAllToolCallsCompleted 完成。
                        setChatHistoryList(markToolCallCompleted(list, tcEnd.toolCallId, output || undefined));
                    }
                }
                break;
            }
            case AgUiEventType.TOOL_CALL_RESULT: {
                const resultEvent = agEvent;
                let output = resultEvent.content;
                if (!output && resultEvent.rawEvent?.result != null) {
                    const raw = resultEvent.rawEvent.result;
                    output = typeof raw === 'string' ? raw : JSON.stringify(raw);
                }
                if (resultEvent.toolCallId) {
                    // RESULT 也意味着工具已执行完，同步标记完成
                    setChatHistoryList(markToolCallCompleted(getChatHistoryList(), resultEvent.toolCallId, output));
                }
                break;
            }
            case AgUiEventType.CRASHCLAW_HTML_PREVIEW: {
                const htmlEvent = agEvent;
                commitCurrentAnswer();
                commitCurrentThinking();
                if (htmlEvent.filePath) {
                    setChatHistoryList(upsertHtmlPreviewItem(getChatHistoryList(), {
                        previewId: htmlEvent.htmlPreviewId,
                        filePath: htmlEvent.filePath,
                        title: htmlEvent.title || htmlEvent.filePath,
                        html: htmlEvent.html,
                        error: htmlEvent.error,
                    }));
                }
                break;
            }
            case AgUiEventType.CRASHCLAW_FILE_DELIVERY: {
                const fileEvent = agEvent;
                commitCurrentAnswer();
                commitCurrentThinking();
                if (fileEvent.filePath) {
                    setChatHistoryList(upsertFileDeliveryItem(getChatHistoryList(), {
                        deliveryId: fileEvent.deliveryId,
                        filePath: fileEvent.filePath,
                        title: fileEvent.title || fileEvent.filePath,
                        mimeType: fileEvent.mimeType,
                        content: fileEvent.content,
                        contentB64: fileEvent.contentB64,
                        error: fileEvent.error,
                    }));
                }
                break;
            }
            case AgUiEventType.RUN_FINISHED: {
                commitCurrentThinking();
                commitCurrentAnswer();
                // 兜底：把所有仍在运行中的 tool call 标记为已完成。
                // 防止 CoPaw 没发非空 output 导致 TOOL_CALL_END 缺失，卡片永远卡在 "运行中"。
                setChatHistoryList(markAllToolCallsCompleted(getChatHistoryList()));
                setIsAnswerOutputting(false);
                scheduleFetchCopawSessions();
                // 实时 SSE 流出于协议限制可能没下发 tool 的 output / arguments，
                // 会话结束后从历史接口回填，让用户点开卡片就能看到结果，无需重新进入会话。
                scheduleBackfillToolCallOutputs();
                // 聊天刚结束，Bridge 可能已经异步触发了 MCP 懒重启（idle trigger），
                // 此时再查一次 MCP 状态，失败的话弹 toast 给用户看
                checkMcpRestartStatus();
                break;
            }
            case AgUiEventType.RUN_ERROR: {
                setIsAnswerOutputting(false);
                message.error(ze('CrashClaw 对话出错，请重试', 'CrashClaw chat error, please retry'));
                scheduleFetchCopawSessions();
                // 聊天出错的可能原因之一就是 gateway 正好在重启中且超时，这时查一下状态
                // 把真正的失败原因（MCP restart failed）暴露出来
                checkMcpRestartStatus();
                break;
            }
            default:
                break;
        }
    }
    /**
     * 处理附件发送：将文件以 base64 传给 Bridge，同时以卡片式展示在聊天区。
     * 消息文本只保留用户输入，不再把 [用户发送文件] 描述塞进去。
     */
    async function handleAttach(files, text) {
        // 将文件以 base64 形式传给 Bridge，Bridge 负责写入工作目录
        const base64Files = await Promise.all(files.map(async (f) => {
            const b64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result.split(',')[1]);
                reader.onerror = reject;
                reader.readAsDataURL(f);
            });
            return { name: f.name, mimeType: f.type, data: b64 };
        }));
        // 附件元信息用于前端展示
        const fileInfos = files.map((f) => ({
            name: f.name,
            mimeType: f.type,
            size: f.size,
        }));
        sendMessage(text.trim(), base64Files, fileInfos);
    }
    async function sendMessage(queryText, attachedFiles, fileInfos, mapping) {
        if (getIsAnswerOutputting()) {
            stopCurrentChat();
        }
        if (!selectedWorkspaceUid) {
            message.warning(ze('请先选择工作区', 'Please select a workspace first'));
            return;
        }
        // 离线守卫：选中工作区非 HEALTHY 时不发起聊天（输入框已禁用，这里兜住脚本化调用路径）
        if (selectedWorkspaceOffline) {
            message.warning(ze('工作区离线，无法发送', 'Workspace offline, cannot send'));
            return;
        }
        // 附件 + 消息拆成两条 message 展示：
        // 1) 附件卡片消息（走 AttachedFilesPanel 渲染分支，text 为空不影响展示）
        // 2) 用户文字消息（仅当有文字时插入，走普通气泡渲染分支）
        // 这样避免附件卡片吞掉用户文字、或用户文字挤进附件卡片无法展示。
        const newItems = [];
        if (fileInfos && fileInfos.length > 0) {
            newItems.push({ isBotNotUser: false, text: '', attachedFiles: fileInfos });
        }
        if (queryText) {
            newItems.push({ isBotNotUser: false, text: queryText });
        }
        // 兜底：既无附件也无文字（理论上不会走到，sendMessage 入口已校验）
        if (newItems.length === 0) {
            newItems.push({ isBotNotUser: false, text: queryText });
        }
        setChatHistoryList([...getChatHistoryList(), getCurrentAnswerInfo(), getCurrentThinkingInfo(), ...newItems].filter((x) => !!x));
        const randomId = String(Math.random());
        setCurrentAnswerRandomId(randomId);
        setCurrentAnswerInfo({ isBotNotUser: true, text: '', isLoading: true });
        setIsAnswerOutputting(true);
        const ac = new AbortController();
        setAbortController(ac);
        const url = `${API_PREFIX}/chat`;
        // 有附件但无文字时，用文件名列表作为 message 兜底，避免后端校验 message 非空失败。
        const messageText = queryText
            || (attachedFiles && attachedFiles.length > 0
                ? attachedFiles.map((f) => f.name).join(', ')
                : '');
        const params = {
            workspaceUid: selectedWorkspaceUid,
            sessionId: mapping?.sessionId || getClawSessionId() || clawSessionId,
            message: messageText,
        };
        if (mapping?.appId && mapping?.issueId) {
            params.appId = mapping.appId;
            params.issueId = mapping.issueId;
        }
        if (clawActiveIntention) {
            params.intention = clawActiveIntention;
        }
        // 模型切换已通过 /updateWorkspaceModel 持久化到 Agent + config_sync 推送给 Bridge，
        // 聊天请求不再携带 specifiedModelName。
        // 附件：文件 base64 数据传给 Bridge，Bridge 负责写入工作目录
        if (attachedFiles && attachedFiles.length > 0) {
            params.attachedFiles = attachedFiles;
        }
        /**
         * 把错误统一转成内联红色气泡 + 重试按钮，避免后端 URL/堆栈通过 antd Modal/notification 弹给用户。
         */
        const options = {
            signal: ac.signal,
            statusCodeToCallback: {
                500: () => {
                    message.error(ze('服务接口出现错误', 'Server error'));
                },
            },
            onmessage: (e) => onReceiveSSEMessage(randomId, e),
        };
        try {
            await RestHelper.postEventStream(url, params, options);
            // SSE 流正常关闭：若没收到 RUN_FINISHED，等同于 Bridge 静默断开，需要兜底清理 loading。
            if (getIsAnswerOutputting()) {
                const answer = getCurrentAnswerInfo();
                const thinking = getCurrentThinkingInfo();
                const hasContent = (!!answer?.text && answer.text.trim() !== '') ||
                    (!!thinking?.thinking?.text && thinking.thinking.text.trim() !== '');
                if (hasContent) {
                    commitCurrentThinking();
                    commitCurrentAnswer();
                }
                else {
                    setCurrentAnswerInfo(null);
                    setCurrentThinkingInfo(null);
                    message.error(ze('CrashClaw 分析超时或未返回内容，请稍后在该工作区重试', 'CrashClaw timed out or returned no content. Retry later in this workspace.'));
                }
                setIsAnswerOutputting(false);
            }
            if (mapping?.appId && mapping?.issueId) {
                bumpIssueAnalysisRefresh(mapping.issueId);
            }
        }
        catch (error) {
            if (error.name !== 'AbortError') {
                console.error('[CrashClawChat] Chat error:', error);
                setCurrentAnswerInfo(null);
                setCurrentThinkingInfo(null);
                setIsAnswerOutputting(false);
                const raw = `${error?.message || ''} ${error?.description || ''}`.toLowerCase();
                // 后端已改为专属 errorCode=BridgeOffline（原裸 IllegalArgument），文本匹配保留兜底状态漂移场景
                if (error?.errorCode === 'BridgeOffline' || (raw.includes('bridge') && (raw.includes('未连接') || raw.includes('not connected') || raw.includes('offline') || raw.includes('离线')))) {
                    message.error(ze('CrashClaw Bridge 离线，请检查工作区连接后重试', 'CrashClaw Bridge is offline. Check the workspace connection and retry.'));
                }
                else if (raw.includes('timeout') || raw.includes('超时')) {
                    message.error(ze('CrashClaw 分析超时，请稍后在该工作区重试', 'CrashClaw analysis timed out. Retry later in this workspace.'));
                }
                else {
                    message.error(ze('CrashClaw 发送失败，请重试', 'Failed to send CrashClaw message, please retry'));
                }
            }
        }
        finally {
            setAbortController(null);
        }
    }
    // ============================================================
    // 导航
    // ============================================================
    function handleGoToConfig(workspaceUid) {
        const uid = workspaceUid || selectedWorkspaceUid;
        window.open(buildCrashClawConfigUrl(uid || undefined), '_blank');
    }
    /** 离线提示条「重试」：重新拉取工作区列表；恢复在线后由恢复 effect 补拉会话与 MCP 状态 */
    const handleWorkspaceRetry = useCallback(async () => {
        if (isControlledWorkspaces) {
            // 受控模式（统一工作区选择器）：列表由父组件提供，触发全局刷新信号让父组件重拉 listWorkspaces
            refreshCrashClawWorkspaces();
            return;
        }
        await fetchWorkspaces(true);
    }, [isControlledWorkspaces, fetchWorkspaces, refreshCrashClawWorkspaces]);
    // ============================================================
    // 渲染
    // ============================================================
    const bubbleDomList = finalChatHistoryList.map((item, i) => {
        if (item.thinking) {
            return _jsx(ThinkingPanel, { thinking: item.thinking }, i);
        }
        if (item.toolCall) {
            return _jsx(ToolCallPanel, { toolCall: item.toolCall }, i);
        }
        if (item.htmlPreview) {
            return _jsx(HtmlPreviewPanel, { htmlPreview: item.htmlPreview }, i);
        }
        if (item.fileDelivery) {
            return _jsx(FileDeliveryPanel, { fileDelivery: item.fileDelivery }, i);
        }
        if (item.attachedFiles && item.attachedFiles.length > 0) {
            return _jsx(AttachedFilesPanel, { files: item.attachedFiles }, i);
        }
        const displayText = item.isBotNotUser
            ? sanitizeAssistantMarkdownText(item.text)
            : item.text;
        return (_jsx(AiChatBotBubble, { messageId: undefined, conversationId: undefined, isBotNotUser: !!item.isBotNotUser, text: displayText, isLoading: !!item.isLoading, messageTimeInfo: undefined, childElement: undefined, customContentTextRenderer: undefined }, i));
    });
    const selectedDisplayName = selectedWorkspace
        ? getWorkspaceDisplayName(selectedWorkspace)
        : '';
    const truncatedWorkspaceName = selectedWorkspace
        ? (selectedDisplayName.length > 20
            ? selectedDisplayName.slice(0, 20) + '…'
            : selectedDisplayName)
        : ze('选择工作区', 'Select workspace');
    const [wsPopoverOpen, setWsPopoverOpen] = useState(false);
    const workspacePopoverContent = (_jsxs("div", { className: scss.clawWsPopover, children: [_jsx("div", { className: scss.clawWsPopoverList, children: workspaces.map(ws => {
                    const isOnline = ws.healthStatus === 'HEALTHY';
                    const isSelected = ws.workspaceUid === selectedWorkspaceUid;
                    const displayName = getWorkspaceDisplayName(ws);
                    const owned = isWorkspaceOwner(ws);
                    return (_jsxs("div", { className: `${scss.clawWsPopoverItem} ${isSelected ? scss.clawWsPopoverItemActive : ''} ${!isOnline ? scss.clawWsPopoverItemDisabled : ''}`, onClick: () => {
                            if (!isOnline)
                                return;
                            setSelectedWorkspaceUid(ws.workspaceUid);
                            setWsPopoverOpen(false);
                        }, children: [_jsx("div", { className: scss.clawWsPopoverItemName, children: displayName }), !owned && (_jsx(Tag, { color: "purple", style: { margin: 0, fontSize: 11, lineHeight: '18px', padding: '0 6px' }, children: ze('共享', 'Shared') })), !isOnline && (_jsx(Tag, { color: ws.healthStatus === 'UNHEALTHY' ? 'red' : 'default', style: { margin: 0, fontSize: 11, lineHeight: '18px', padding: '0 6px' }, children: ws.healthStatus === 'UNHEALTHY'
                                    ? ze('离线', 'Offline')
                                    : ze('未知', 'Unknown') })), ws.agentName && (_jsx("div", { className: scss.clawWsPopoverItemAgent, children: ws.agentName })), isOnline && isSelected && (_jsx(CheckOutlined, { className: scss.clawWsPopoverItemCheck }))] }, ws.workspaceUid));
                }) }), _jsx(Divider, { style: { margin: '8px 0' } }), _jsxs("div", { className: scss.clawWsPopoverFooter, children: [_jsx("span", { className: scss.clawWsPopoverFooterHint, children: ze('没有需要的工作区？', 'Need a different workspace?') }), _jsx("a", { className: scss.clawWsPopoverFooterLink, onClick: (e) => {
                            e.preventDefault();
                            setWsPopoverOpen(false);
                            handleGoToConfig();
                        }, children: ze('去管理', 'Manage') })] })] }));
    const workspacePickerBtn = (hasWorkspaces && !hideInternalPicker) ? (_jsx(Popover, { content: workspacePopoverContent, trigger: "click", open: wsPopoverOpen, onOpenChange: (open) => {
            setWsPopoverOpen(open);
            if (open) {
                fetchWorkspaces(true);
            }
        }, placement: "topLeft", overlayStyle: { width: 260 }, children: _jsxs("div", { className: scss.clawWsPickerBtn, children: [_jsx(CloudOutlined, { className: scss.clawWsPickerIcon }), _jsx("span", { className: scss.clawWsPickerName, children: truncatedWorkspaceName })] }) })) : null;
    // 有配置过工作区但全部不在线时，在内容区顶部展示警告横幅，引导用户检查 Bridge 或跳转工作区管理
    const offlineAlertBanner = hasWorkspaces && !hasOnlineWorkspace ? (_jsx(Alert, { type: "warning", showIcon: true, banner: true, message: _jsxs("span", { children: [ze('当前所有工作区均不在线，无法发送消息。请确保 Bridge 程序正在运行，或', 'All workspaces are offline. Please ensure Bridge is running, or '), _jsx("a", { onClick: (e) => { e.preventDefault(); handleGoToConfig(); }, style: { fontWeight: 500 }, children: ze('前往工作区管理', 'manage workspaces') })] }) })) : null;
    // 当前选中工作区离线（但可能存在其它在线工作区）时的行内提示条：
    // 展示属主 + 重试（重新拉 listWorkspaces 按新状态收敛）+ 前往配置（复用 buildCrashClawConfigUrl）
    const offlineOwnerDisplay = selectedWorkspace?.ownerRtx || selectedWorkspace?.ownerLocalUserId || '';
    const workspaceOfflineBanner = selectedWorkspaceOffline && selectedWorkspace ? (_jsx(Alert, { type: "warning", showIcon: true, banner: true, message: _jsxs("span", { children: [ze(`工作区「${truncatedWorkspaceName}」${offlineOwnerDisplay ? `（属主 ${offlineOwnerDisplay}）` : ''}离线，无法发送消息或加载会话历史。请确认属主机上的 Bridge 程序正在运行，或`, `Workspace "${truncatedWorkspaceName}"${offlineOwnerDisplay ? ` (owner ${offlineOwnerDisplay})` : ''} is offline. Unable to send messages or load chat history. Please ensure the Bridge is running on the owner's machine, or `), _jsx("a", { onClick: (e) => { e.preventDefault(); void handleWorkspaceRetry(); }, style: { fontWeight: 500 }, children: ze('重试', 'retry') }), ' / ', _jsx("a", { onClick: (e) => { e.preventDefault(); handleGoToConfig(selectedWorkspaceUid); }, style: { fontWeight: 500 }, children: ze('前往配置', 'go to settings') })] }) })) : null;
    // 内容区顶部横幅二选一：选中工作区离线优先展示行内提示条，否则回退「全部离线」横幅
    const topOfflineBanner = selectedWorkspaceOffline ? workspaceOfflineBanner : offlineAlertBanner;
    // 模型选择仅在已选中的在线 CrashClaw 工作区展示；外观与工作区切换保持一致。
    const [modelPopoverOpen, setModelPopoverOpen] = useState(false);
    const selectedModelLabel = modelOptions.find((item) => item.value === selectedModel)?.label || selectedModel;
    const modelPickerContent = (_jsx("div", { className: scss.clawModelPopover, children: _jsx("div", { className: scss.clawModelPopoverList, children: modelOptions.map((model) => {
                const isSelected = model.value === selectedModel;
                return (_jsxs("div", { className: `${scss.clawModelPopoverItem} ${isSelected ? scss.clawModelPopoverItemActive : ''}`, onClick: (event) => {
                        event.stopPropagation();
                        handleModelChange(model.value);
                        setModelPopoverOpen(false);
                    }, children: [_jsx("span", { className: scss.clawModelPopoverItemName, children: model.label }), isSelected && _jsx(CheckOutlined, { className: scss.clawWsPopoverItemCheck })] }, model.value));
            }) }) }));
    const modelPickerBtn = selectedWorkspaceUid
        && selectedWorkspace?.healthStatus === 'HEALTHY'
        && (selectedWorkspace?.agentType === 'codebuddy' || selectedWorkspace?.agentType === 'hermes') ? (_jsx(Popover, { content: modelPickerContent, trigger: "click", open: modelPopoverOpen, onOpenChange: setModelPopoverOpen, placement: "topLeft", overlayStyle: { width: 260 }, destroyTooltipOnHide: false, children: _jsxs("div", { className: scss.clawModelPickerBtn, onClick: (event) => event.stopPropagation(), children: [_jsx(BulbOutlined, { className: scss.clawModelPickerIcon }), _jsx("span", { className: scss.clawModelPickerName, title: selectedModelLabel, children: selectedModelLabel }), _jsx(DownOutlined, { className: scss.clawModelPickerChevron })] }) })) : null;
    const leftCluster = (_jsxs("div", { className: scss.clawInputPickerCluster, children: [(hideInternalPicker ? externalLeftElement : workspacePickerBtn), modelPickerBtn] }));
    const inputArea = (_jsx("div", { style: { padding: '16px' }, children: _jsx(AiChatBotInputBox, { disableSend: isAnswerOutputting || !selectedWorkspaceUid || !hasOnlineWorkspace || selectedWorkspaceOffline, disabled: selectedWorkspaceOffline, placeholder: selectedWorkspaceOffline
                ? ze('工作区离线，无法发送', 'Workspace offline')
                : undefined, isStreaming: isAnswerOutputting, onStop: stopCurrentChat, onSend: (v) => sendMessage(v), onAttach: (files, text) => handleAttach(files, text), leftElement: leftCluster }) }));
    // --- 主渲染 ---
    if (loadingWorkspaces) {
        return _jsx("div", { className: scss.homePageBody, children: _jsx("div", { style: { textAlign: 'center', padding: 40, color: '#999' }, children: ze('加载中…', 'Loading…') }) });
    }
    if (!hasWorkspaces) {
        return (_jsxs("div", { className: scss.homePageBody, children: [_jsxs("div", { className: scss.welcomeBanner, children: [_jsxs("div", { className: scss.welcomeText, children: [_jsx("div", { className: scss.welcomeGreeting, children: "Hi~" }), _jsx("div", { className: scss.welcomeTitle, children: ze('智能工作流引擎已就绪！', 'Smart Workflow Engine is Ready!') })] }), _jsx("div", { className: scss.welcomeLogo, children: _jsx(CrashClawWelcomeIcon, { style: { width: 56, height: 56 } }) })] }), _jsxs("div", { className: scss.guideCard, children: [_jsxs("div", { className: scss.guideCardHeader, children: [_jsx(ExclamationCircleOutlined, {}), _jsx("span", { children: ze('完成配置后即可使用 CrashClaw', 'Complete setup to use CrashClaw') })] }), _jsx("div", { className: scss.guideCardDesc, children: ze('只需两步即可启用 CrashClaw 全链路工作流。第一步为必须完成的核心配置，第二步可按需选配。', 'Just two steps to enable CrashClaw workflow. Step 1 is required, Step 2 is optional.') }), _jsxs("div", { className: scss.guideSteps, children: [_jsxs("div", { className: scss.guideStep, children: [_jsx("span", { className: scss.guideStepNum, children: "1" }), _jsxs("div", { className: scss.guideStepContent, children: [_jsxs("div", { className: scss.guideStepTitleRow, children: [_jsx("span", { className: scss.guideStepTitle, children: ze('配置 Workspace', 'Setup Workspace') }), _jsx("span", { className: scss.guideStepBadgeRequired, children: ze('必须', 'Required') })] }), _jsx("span", { className: scss.guideStepDesc, children: ze('创建工作区并关联 Agent，复制一键安装命令在开发机中执行，即可让 Agent 接入你的研发环境', 'Create a workspace, link an Agent, and run the one-click install command on your dev machine to connect') })] })] }), _jsxs("div", { className: scss.guideStep, children: [_jsx("span", { className: scss.guideStepNum, children: "2" }), _jsxs("div", { className: scss.guideStepContent, children: [_jsxs("div", { className: scss.guideStepTitleRow, children: [_jsx("span", { className: scss.guideStepTitle, children: ze('扩展能力配置', 'Extended Configuration') }), _jsx("span", { className: scss.guideStepBadgeOptional, children: ze('选配', 'Optional') })] }), _jsx("span", { className: scss.guideStepDesc, children: ze('按需接入 TAPD MCP、工蜂 MCP 等外部服务，或配置个人 Skills，进一步扩展工作流能力', 'Optionally connect TAPD MCP, Gongfeng MCP, or configure personal Skills to extend capabilities') })] })] })] }), _jsxs(Button, { type: "primary", block: true, onClick: () => handleGoToConfig(), children: [_jsx(ArrowRightOutlined, {}), ze('前往配置', 'Go to Settings')] })] }), _jsx("div", { style: { flexGrow: 1 } })] }));
    }
    // 加载历史消息中
    if (loadingSessionMessages) {
        return (_jsxs(_Fragment, { children: [topOfflineBanner, _jsx("div", { className: scss.homePageBody, children: _jsx("div", { style: { textAlign: 'center', padding: 40, color: '#999' }, children: ze('加载会话历史中…', 'Loading chat history…') }) }), inputArea] }));
    }
    if (finalChatHistoryList.length > 0) {
        return (_jsxs(_Fragment, { children: [topOfflineBanner, _jsx(AiDrawerAutoScrollBody, { forceScrollToBottomTrigger: undefined, children: bubbleDomList }), inputArea] }));
    }
    // 进入子场景后的子页面：与 AI Agent 子场景页布局对齐
    // 返回按钮放到 AiChatBotEx 的 header 左侧（通过 onActiveIntentionChange 通知父）
    // 推荐问题紧跟标题/副标题之后，再 flex grow 占满剩余空间到底部 input
    if (clawActiveIntention) {
        const activeCard = CRASHCLAW_SCENARIO_CARDS.find(c => c.intention === clawActiveIntention);
        if (activeCard) {
            const IconComp = activeCard.icon;
            const quickSuggests = CLAW_QUICK_SUGGEST_MAP[clawActiveIntention] || [];
            return (_jsxs(_Fragment, { children: [topOfflineBanner, _jsxs("div", { className: scss.scenarioSubPageBody, children: [_jsxs("div", { className: scss.scenarioSubPageContent, children: [_jsx("div", { className: scss.scenarioSubPageIcon, children: _jsx(IconComp, { style: { width: 64, height: 64 } }) }), _jsx("div", { className: scss.scenarioSubPageTitle, children: activeCard.pageTitle }), _jsx("div", { className: scss.scenarioSubPageSubtitle, children: activeCard.pageSubtitle })] }), quickSuggests.length > 0 ? (_jsxs("div", { className: scss.suggestSection, children: [_jsx("div", { className: scss.suggestHeader, children: _jsx("span", { className: scss.suggestTitle, children: ze('你还可以直接问我：', 'You can also ask me:') }) }), _jsx("div", { className: scss.suggestList, children: quickSuggests.map((q) => (_jsxs("button", { className: scss.suggestItem, onClick: () => sendMessage(q), disabled: isAnswerOutputting || !selectedWorkspaceUid || !hasOnlineWorkspace || selectedWorkspaceOffline, children: [_jsx(BulbOutlined, { className: scss.suggestIcon }), _jsx("span", { className: scss.suggestText, children: q })] }, q))) })] })) : null, _jsx("div", { style: { flexGrow: 1 } }), inputArea] })] }));
        }
    }
    return (_jsxs("div", { className: scss.homePageBody, children: [topOfflineBanner, _jsxs("div", { className: scss.welcomeBanner, children: [_jsxs("div", { className: scss.welcomeText, children: [_jsx("div", { className: scss.welcomeGreeting, children: "Hi~" }), _jsx("div", { className: scss.welcomeTitle, children: ze('智能工作流引擎已就绪！', 'Smart Workflow Engine is Ready!') })] }), _jsx("div", { className: scss.welcomeLogo, children: _jsx(CrashClawWelcomeIcon, { style: { width: 56, height: 56 } }) })] }), _jsx("div", { className: scss.scenarioCardList, children: CRASHCLAW_SCENARIO_CARDS.map((card) => {
                    const IconComp = card.icon;
                    return (_jsxs("div", { className: scss.scenarioCard, onClick: () => setClawActiveIntention(card.intention), children: [_jsx("div", { className: scss.scenarioCardIcon, children: _jsx(IconComp, { style: { width: 40, height: 40 } }) }), _jsxs("div", { className: scss.scenarioCardContent, children: [_jsx("div", { className: scss.scenarioCardTitle, children: card.title }), _jsx("div", { className: scss.scenarioCardSubtitle, children: card.subtitle })] })] }, card.intention));
                }) }), _jsx("div", { style: { flexGrow: 1 } }), inputArea] }));
};
export default CrashClawChatPanel;
